import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import cx from "classnames";
import PropTypes from "prop-types";

import { ReactDefaultProps } from "../../types/react";
import type { InputProps } from "../Input";
import { InputDateRange } from "../Input/InputDateRange";
import { useDateRange } from "./useDateRange";
import Calendar from "./Calendar";
import MobileModalFrame from "./ModalFrame";
import { ContentCalendarDefault } from "../../icons";
import styles from "./DateRangePicker.scss";
import { useClickOutside } from "../hooks/useClickOutside";
import { useMediaQuery, mediaQueries } from "../hooks/useMediaQuery";
import { ActionProvider, DateRangeActionType } from "./useDateRangeAction";

export type DateRangePickerProps = {
  onChange?: (dateRange: [Date | undefined, Date | undefined]) => void;
  renderInput?: ({
    getInputStartProps,
    getInputEndProps,
    getCombinedInputProps,
  }: {
    getInputStartProps: () => InputProps;
    getInputEndProps: () => InputProps;
    getCombinedInputProps: () => InputProps;
  }) => JSX.Element;
  initialCalendarDate?: Date;
  isOpen?: boolean;
};

const defaultProps = {
  onChange: undefined,
  renderInput: undefined,
  initialCalendarDate: undefined,
  isOpen: false,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  DateRangePickerProps,
  typeof defaultProps
> = defaultProps;

const propTypes = {
  onChange: PropTypes.func,
  renderInput: PropTypes.func,
  initialCalendarDate: PropTypes.instanceOf(Date),
  isOpen: PropTypes.bool,
} as const;

const DateRangePickerWithRef = (
  props: DateRangePickerProps,
  forwardedRef: React.ComponentPropsWithRef<"div">["ref"]
) => {
  const { onChange, renderInput, initialCalendarDate, isOpen } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(forwardedRef, () => wrapperRef.current as HTMLDivElement);

  const firstUpdate = useRef(true);

  const {
    selectedStartDate,
    selectedEndDate,
    displayDatePicker,
    getCombinedInputProps,
    getInputEndProps,
    getInputStartProps,
    getCalendarProps,
    dispatch,
  } = useDateRange(initialCalendarDate || new Date());

  const isViewportS = useMediaQuery(mediaQueries.s);

  useClickOutside(wrapperRef, () => {
    if (!isViewportS) dispatch({ type: DateRangeActionType.HidePicker });
  });

  const handlePickerClosing = useCallback(
    (e: GlobalEventHandlersEventMap["keydown"]) => {
      if (e.key === "Escape")
        dispatch({ type: DateRangeActionType.HidePicker });
    },
    []
  );

  useEffect(() => {
    dispatch({ type: DateRangeActionType.RecalculatePreviewKeyboardDate });
    document.addEventListener("keydown", handlePickerClosing);

    return () => {
      document.removeEventListener("keydown", handlePickerClosing);
    };
  }, []);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
    } else if (onChange) {
      onChange([selectedStartDate, selectedEndDate]);
    }
  }, [selectedStartDate, selectedEndDate]);

  return (
    <ActionProvider value={dispatch}>
      <div ref={wrapperRef}>
        {!renderInput && (
          <InputDateRange
            customClass={styles["date-range__input-wrapper"]}
            button={{
              icon: ContentCalendarDefault,
              label: `Datumsauswahl ${
                displayDatePicker ? "schließen" : "öffnen"
              }`,
              onClick: () =>
                dispatch({ type: DateRangeActionType.TogglePicker }),
              customClass: styles["input-button"],
            }}
            {...getCombinedInputProps()}
          >
            Zeitraum
          </InputDateRange>
        )}
        {renderInput &&
          renderInput({
            getCombinedInputProps,
            getInputEndProps,
            getInputStartProps,
          })}
        {(isOpen || displayDatePicker) && (
          <div
            className={cx(styles["date-range-picker"], styles["month-border"])}
          >
            <Calendar {...getCalendarProps()} />
          </div>
        )}
        <div className={cx(styles["date-range-picker-mobile"])}>
          {isViewportS && (
            <MobileModalFrame
              displayDatePicker={isOpen || displayDatePicker}
              hidePicker={() =>
                dispatch({ type: DateRangeActionType.HidePicker })
              }
            >
              <Calendar {...getCalendarProps()} />
            </MobileModalFrame>
          )}
        </div>
      </div>
    </ActionProvider>
  );
};

export const DateRangePicker = forwardRef(DateRangePickerWithRef);

DateRangePicker.displayName = "DateRangePicker";

DateRangePicker.propTypes = propTypes;

DateRangePicker.defaultProps = validatedDefaultProps;

export default DateRangePicker;
